import { supabase } from '@/integrations/supabase/client';

export interface AnalysisWarning {
  level: 'info' | 'warning' | 'error';
  schemaType: string;
  message: string;
  suggestion: string;
}

export interface GeneratedSchema {
  title: string;
  type: string;
  jsonLd: object;
  isNew: boolean;
  isImproved: boolean;
  originalSchema?: object; // Original schema for comparison when isImproved=true
}

export interface AnalysisResult {
  url: string;
  pageType: string;
  title: string;
  existingSchemas: string[];
  suggestedSchemas: string[];
  generatedSchemas: GeneratedSchema[];
  warnings?: AnalysisWarning[];
}

export interface AnalysisResponse {
  success: boolean;
  error?: string;
  data?: AnalysisResult;
}

export interface BulkAnalysisResponse {
  success: boolean;
  results?: AnalysisResult[];
  errors?: { url: string; error: string }[];
  creditsUsed?: number;
}

export interface CrawlResponse {
  success: boolean;
  error?: string;
  baseUrl?: string;
  urls?: string[];
  totalFound?: number;
  returned?: number;
  maxAllowed?: number;
  fallbackUsed?: boolean;
  sitemapFallbackUsed?: boolean;
  filteredMultilingual?: {
    count: number;
    languages: string[];
  } | null;
}

export async function analyzePageUrl(url: string): Promise<AnalysisResponse> {
  const { data, error } = await supabase.functions.invoke('analyze-page', {
    body: { url },
  });

  if (error) {
    console.error('Error calling analyze-page function:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to analyze page' 
    };
  }

  if (!data?.success) {
    return { 
      success: false, 
      error: data?.error || 'Analysis failed' 
    };
  }

  return {
    success: true,
    data: data.data,
  };
}

export async function analyzePageUrlFree(url: string): Promise<AnalysisResponse> {
  const { data, error } = await supabase.functions.invoke('analyze-page', {
    body: { url, free_trial: true },
  });

  if (error) {
    console.error('Error calling analyze-page function (free trial):', error);
    return { 
      success: false, 
      error: error.message || 'Failed to analyze page' 
    };
  }

  if (!data?.success) {
    return { 
      success: false, 
      error: data?.error || 'Analysis failed' 
    };
  }

  return {
    success: true,
    data: data.data,
  };
}

export async function analyzePageUrls(urls: string[]): Promise<BulkAnalysisResponse> {
  const { data, error } = await supabase.functions.invoke('analyze-page', {
    body: { urls },
  });

  if (error) {
    console.error('Error calling analyze-page function:', error);
    return { 
      success: false, 
      errors: [{ url: 'bulk', error: error.message || 'Failed to analyze pages' }],
    };
  }

  if (!data?.success) {
    return { 
      success: false, 
      errors: [{ url: 'bulk', error: data?.error || 'Analysis failed' }],
    };
  }

  return {
    success: true,
    results: data.results,
    creditsUsed: data.creditsUsed,
  };
}

export async function crawlSite(url: string, limit = 20): Promise<CrawlResponse> {
  const { data, error } = await supabase.functions.invoke('crawl-site', {
    body: { url, limit },
  });

  if (error) {
    console.error('Error calling crawl-site function:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to crawl site' 
    };
  }

  if (!data?.success) {
    return { 
      success: false, 
      error: data?.error || 'Crawl failed' 
    };
  }

  return {
    success: true,
    baseUrl: data.baseUrl,
    urls: data.urls,
    totalFound: data.totalFound,
    returned: data.returned,
    fallbackUsed: data.fallbackUsed || false,
    sitemapFallbackUsed: data.sitemapFallbackUsed || false,
    filteredMultilingual: data.filteredMultilingual || null,
  };
}
